import { formatDateTime, formatFileSize } from '../../lib/utils';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
    DropdownMenuSeparator
} from '../ui/dropdown-menu';
import {
    Eye,
    Download,
    Trash,
    DotsThreeVertical,
    ArrowClockwise,
    ClockCounterClockwise,
    X,
    Check
} from '@phosphor-icons/react';
import { DocumentStatusBadge } from './DocumentStatusBadge';
import { FileIcon } from './FileIcon';

export const DocumentRow = ({
    doc,
    onDownload,
    onPreview,
    onReplace,
    onDelete,
    onValidate,
    onViewVersions,
    canValidate = false,
    canDelete = true
}) => {
    return (
        <div className="flex items-center gap-3 p-3 bg-white border rounded-lg hover:bg-muted/30 transition-colors" data-testid={`document-row-${doc.id}`}>
            <FileIcon contentType={doc.content_type} />

            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                    <p className="font-medium text-sm truncate">{doc.original_filename}</p>
                    {doc.version > 1 && (
                        <Badge variant="secondary" className="text-xs">v{doc.version}</Badge>
                    )}
                </div>
                <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
                    <span>{formatFileSize(doc.size)}</span>
                    <span>•</span>
                    <span>{formatDateTime(doc.uploaded_at)}</span>
                    {doc.uploaded_by_name && (
                        <>
                            <span>•</span>
                            <span>{doc.uploaded_by_name}</span>
                        </>
                    )}
                </div>
            </div>

            <DocumentStatusBadge status={doc.validation_status} />

            <div className="flex items-center gap-1">
                <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => onPreview(doc)}
                    title="Visualizza"
                    data-testid={`preview-doc-${doc.id}`}
                >
                    <Eye className="w-4 h-4" />
                </Button>
                <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => onDownload(doc)}
                    title="Scarica"
                >
                    <Download className="w-4 h-4" />
                </Button>

                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`doc-menu-${doc.id}`}>
                            <DotsThreeVertical className="w-4 h-4" />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => onReplace(doc)}>
                            <ArrowClockwise className="w-4 h-4 mr-2" />
                            Carica nuova versione
                        </DropdownMenuItem>
                        {doc.version_count > 1 && (
                            <DropdownMenuItem onClick={() => onViewVersions(doc)}>
                                <ClockCounterClockwise className="w-4 h-4 mr-2" />
                                Storico versioni ({doc.version_count})
                            </DropdownMenuItem>
                        )}
                        {canValidate && (
                            <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => onValidate(doc, 'approvato')}>
                                    <Check className="w-4 h-4 mr-2 text-green-600" />
                                    Approva
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => onValidate(doc, 'rifiutato')}>
                                    <X className="w-4 h-4 mr-2 text-red-600" />
                                    Rifiuta
                                </DropdownMenuItem>
                            </>
                        )}
                        {canDelete && (
                            <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                    onClick={() => onDelete(doc)}
                                    className="text-red-600 focus:text-red-600"
                                >
                                    <Trash className="w-4 h-4 mr-2" />
                                    Elimina
                                </DropdownMenuItem>
                            </>
                        )}
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>
        </div>
    );
};
